<template>
  <v-layout class="navigation-drawer__layout">
    <v-navigation-drawer
      :rail="isCollapsedNavigation"
      :rail-width="$vuetify.display.lgAndDown ? 59 : 64"
      border="none"
      color="transparent"
      width="280"
      class="navigation-drawer"
      mobile-breakpoint="lg"
    >
      <div class="navigation-drawer__content">
        <v-list-item class="pa-0 navigation-drawer__logo">
          <v-img :src="logoInline" width="136" class="d-block ml-1" />
        </v-list-item>

        <slot name="navigation" />
      </div>

      <e-icon
        class="navigation-drawer__toggle"
        :name="isCollapsedNavigation ? 'openDrawer' : 'closeDrawer'"
        :size="$vuetify.display.lgAndDown ? 26 : 36"
        @click.stop="toggleDrawer"
      />
    </v-navigation-drawer>

    <v-main class="navigation-drawer__main">
      <div v-if="slots.notification" class="navigation-drawer__notification">
        <slot name="notification" />
      </div>
      <div v-if="slots.header" class="navigation-drawer__header">
        <slot name="header" />
      </div>
      <slot name="main" />
    </v-main>

    <div
      v-if="$vuetify.display.lg && !isCollapsedNavigation"
      class="navigation-drawer__overlay"
      @click="toggleDrawer"
    />
  </v-layout>
</template>

<script setup lang="ts">
import logoInline from '@/assets/img/logo-inline.svg';
import { EIcon } from '@/core/components/ui';
import { useLayoutStore } from '@/core/store/layout';

const slots = defineSlots<{
  navigation?: (props: unknown) => void;
  main?: (props: unknown) => void;
  header?: (props: unknown) => void;
  notification?: (props: unknown) => void;
}>();

const { setIsCollapsedNavigation, isCollapsedNavigation } = useLayoutStore();

const toggleDrawer = () => {
  setIsCollapsedNavigation(!isCollapsedNavigation.value);
};
</script>

<style scoped lang="scss">
.navigation-drawer {
  position: fixed !important;
  z-index: $z-index-6 !important;

  display: flex;
  flex-direction: column;

  height: 100vh !important;
  padding: 20px 0 20px 23px;
  overflow: auto;

  direction: rtl;

  @include lg-and-down {
    transition: width 0.14s linear;
  }

  &__layout {
    overflow: initial !important;

    @include lg-and-down {
      &:before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        z-index: 10001;

        width: 280px;

        background: $main-bg-color;

        transform: translateX(-100%);
        transition: transform 0.2s linear;
      }
      &:not(:has(.v-navigation-drawer--rail)) {
        &:before {
          transform: translateX(0);
          transition: transform 0.1s linear;
        }
      }
    }

    @include md-and-down {
      &:before {
        display: none;
      }
    }
  }

  &__logo {
    margin-bottom: 56px;

    @include lg-and-down {
      margin-left: -10px;
    }
  }

  :deep(.v-navigation-drawer__content) {
    display: flex;
    flex-grow: 1;
    flex-direction: column;

    height: auto;
    overflow: visible;

    direction: ltr;
  }

  &__overlay {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: $z-index-5;

    background-color: $overlay-bg;
  }

  &__toggle {
    box-sizing: border-box;

    margin-top: 16px;
    margin-right: 16px;
    margin-left: auto;
    padding-left: 10px;

    color: $text-ordinary-color;

    cursor: pointer;

    @include hover {
      color: $primary-color;
    }

    @include lg-and-down {
      padding-left: 0;
    }
  }

  &__header {
    display: flex;
    align-items: center;

    @include md-and-down {
      padding: 10px $gap-5;
    }

    @include xs {
      padding: 10px $gap-4;
    }
  }

  &__content {
    display: flex;
    flex-grow: 1;
    flex-direction: column;

    min-width: 253px;
  }

  &__main {
    display: flex;
    flex: initial;
    flex-direction: column;

    gap: 16px;

    width: 100%;
    margin-left: 32px;
    padding-top: 16px;
    padding-right: 32px;
    padding-bottom: 24px;

    @include lg-and-down {
      margin-left: 24px;
      padding-right: 24px;
      padding-left: 64px !important;
    }

    @include md-and-down {
      gap: 8px;

      margin-left: 0;
      padding: 0 !important;
    }
  }

  &__notification {
    &:empty {
      display: none;
    }

    @include md-and-down {
      padding: 24px 24px 0;
    }

    @include xs {
      padding: 16px 16px 0;
    }
  }
}
</style>
